// Simplified Project Settings - Matches actual Project.metadata usage
import { getDefaultBoardSettings, ProjectBoardSettings } from './board-column'
import { ProjectTypeEnum } from './project'
import { SavedView } from './user-preferences'

// Simple project configuration stored in Project.metadata
export interface ProjectSettings {
  // Core feature toggles
  features: {
    sprints: boolean
    kanban: boolean
    velocity: boolean
    dependencies: boolean
    milestones: boolean
    buckets?: boolean // optional: stored settings predate this flag
    deployments?: boolean // optional: deployment tracking, off by default
  }

  // Deployment automation (feature-gated by features.deployments): move
  // scope tasks to a status when a deployment completes. Per-environment map
  // keyed by ProjectEnvironment.key; onProdDeployedStatusId is the legacy
  // production-only fallback.
  deployments?: {
    onDeployedStatusByEnv?: Record<string, string | null>
    onProdDeployedStatusId?: string | null
  }

  // UI preferences
  defaultView: 'list' | 'board' | 'calendar' | 'timeline'

  // Status configuration - simple reference to template
  statusTemplateId?: string

  // Status configurations - actual status data
  statusConfigs?: {
    statuses: Array<{
      id: string
      name: string
      color?: string
      statusTypeId: string
      order: number
    }>
    hasCustomStatuses?: boolean
    lastSyncedAt?: string
  }

  // Task column preferences - project-specific
  taskColumnPreferences?: {
    columnVisibility: Record<string, boolean>
    columnOrders: Record<string, number>
    columnWidths: Record<string, number>
    lastUpdatedAt?: string
  }

  // Document column preferences - project-specific shared layout
  documentColumnPreferences?: {
    columnVisibility: Record<string, boolean>
    columnOrders: Record<string, number>
    columnWidths: Record<string, number>
    lastUpdatedAt?: string
  }

  // Task views - project-scoped saved views (personal and shared)
  taskViews?: ProjectTaskViewPreferences

  // Project type specific settings
  typeSettings?: {
    // Scrum settings
    sprintDuration?: number // days
    storyPointScale?: number[]

    // Kanban settings
    wipLimits?: Record<string, number>

    // Waterfall settings
    requirePhaseGates?: boolean
  }

  // Board view settings (column configuration)
  boardSettings?: ProjectBoardSettings
}

// Factory function for default settings based on project type
export function getDefaultProjectSettings(
  projectType: ProjectTypeEnum,
): ProjectSettings {
  const baseSettings: ProjectSettings = {
    features: {
      sprints: false,
      kanban: false,
      velocity: false,
      dependencies: false,
      milestones: true,
      buckets: true,
      deployments: false,
    },
    defaultView: 'list',
    boardSettings: getDefaultBoardSettings(projectType),
  }

  switch (projectType) {
    case ProjectTypeEnum.SCRUM:
      return {
        ...baseSettings,
        features: {
          ...baseSettings.features,
          sprints: true,
          velocity: true,
        },
        defaultView: 'board',
        typeSettings: {
          sprintDuration: 14, // 2 weeks
          storyPointScale: [1, 2, 3, 5, 8, 13, 21],
        },
        boardSettings: getDefaultBoardSettings(ProjectTypeEnum.SCRUM),
      }

    case ProjectTypeEnum.KANBAN:
      return {
        ...baseSettings,
        features: {
          ...baseSettings.features,
          kanban: true,
        },
        defaultView: 'board',
        typeSettings: {
          wipLimits: {
            'in-progress': 5,
            review: 3,
          },
        },
        boardSettings: getDefaultBoardSettings(ProjectTypeEnum.KANBAN),
      }

    case ProjectTypeEnum.WATERFALL:
      return {
        ...baseSettings,
        features: {
          ...baseSettings.features,
          dependencies: true,
          milestones: true,
        },
        defaultView: 'timeline',
        typeSettings: {
          requirePhaseGates: true,
        },
        boardSettings: getDefaultBoardSettings(ProjectTypeEnum.WATERFALL),
      }

    case ProjectTypeEnum.BASIC:
    default:
      return baseSettings
  }
}

// Helper functions for checking project settings
export function hasSprintsEnabled(settings: ProjectSettings): boolean {
  return settings.features?.sprints || false
}

export function hasBucketsEnabled(settings: ProjectSettings): boolean {
  // Buckets are a standard project capability. Treat the flag as enabled when
  // it is absent so projects created before the setting was introduced receive
  // the current default without requiring a metadata migration.
  return settings.features?.buckets !== false
}

export function hasKanbanEnabled(settings: ProjectSettings): boolean {
  return settings.features?.kanban || false
}

export function hasDeploymentsEnabled(settings: ProjectSettings): boolean {
  // Deployment tracking is opt-in: absent flag means disabled
  return settings.features?.deployments === true
}

export function hasVelocityEnabled(settings: ProjectSettings): boolean {
  return settings.features?.velocity || false
}

export function hasDependenciesEnabled(settings: ProjectSettings): boolean {
  return settings.features?.dependencies || false
}

export function hasMilestonesEnabled(settings: ProjectSettings): boolean {
  return settings.features?.milestones !== false // Default to true
}

// Project-scoped task view preferences
// Views are stored per-user within a project to allow personal views
// Shared views are stored with userId: null
export interface ProjectTaskViewPreferences {
  // Personal views keyed by userId
  userViews: Record<string, SavedView[]>
  // Shared views visible to all project members
  sharedViews: SavedView[]
  // Active view ID per user
  activeViewIds: Record<string, string>
  // Default view for new users (from shared views)
  defaultViewId?: string
  lastUpdatedAt?: string
}
